import emailjs from '@emailjs/browser';

// Initialize EmailJS with environment variables
const EMAILJS_CONFIG = {
  serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID,
  templateId: 'template_6mu3s5i', // Use the default template
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
  adminEmail: 'hchs1923@gmail.com' // Admin email for RSVP notifications
};

// Initialize EmailJS
emailjs.init(EMAILJS_CONFIG.publicKey);

const sendEmailSafe = async (templateParams) => {
  try {
    return await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templateId,
      templateParams
    );
  } catch (error) {
    return { __error: error };
  }
};

export const sendRSVPEmail = async (rsvpData) => {
  try {
    const guests = Array.isArray(rsvpData.guests) ? rsvpData.guests : [];
    const guestDetails = guests.length === 0
      ? 'No guest'
      : guests.map((guest, index) => {
        const status = (guest.type === 'member') ? 'Member' : 'Non-member';
        const dietary = guest.dietary ? ` (Dietary: ${guest.dietary})` : '';
        return `Guest ${index + 1}: ${guest.name} — ${status}${dietary}`;
      }).join('\n');

    const isPayLater = String(rsvpData?.paymentDetails?.status || '').toUpperCase() === 'PAY_LATER';
    const paymentTime = new Date(rsvpData?.paymentDetails?.create_time || rsvpData?.paymentDetails?.timestamp || Date.now()).toLocaleString();

    const memberRecognized = !!rsvpData.isHCHSMember;
    const manuallySelectedMember = String(rsvpData.primaryTicketType) === 'member' && !memberRecognized;
    const recognizedSelectedNonMember = memberRecognized && String(rsvpData.primaryTicketType) === 'nonMember';
    const memberStatusAdmin = manuallySelectedMember
      ? 'Member (selected manually; email not recognized as member)'
      : recognizedSelectedNonMember
        ? 'Non-member (selected manually; email recognized as member)'
        : (memberRecognized ? 'Member' : 'Non-member');

    const emailContent = `
New RSVP Received

Name: ${rsvpData.name}
Email: ${rsvpData.email}
Primary Dietary: ${rsvpData.primaryDietary ? rsvpData.primaryDietary : 'N/A'}
Event: ${rsvpData.eventTitle}
Date: ${rsvpData.eventDate}
Guest Count: ${rsvpData.guestCount}
Membership Status: ${memberStatusAdmin}

Guest Details:
${guestDetails}

${isPayLater ? `Payment Method: PAY LATER
Amount Due At Event: $${rsvpData.total.toFixed(2)}` : `Total Amount: $${rsvpData.total.toFixed(2)}`}
Payment ID: ${rsvpData.paymentDetails.id}
Payment Time: ${paymentTime}
Test Mode: ${rsvpData.paymentDetails.isTestMode ? 'Yes' : 'No'}
    `.trim();

    const confirmationContent = `
Thank you for your RSVP!

Your RSVP has been confirmed for:
Event: ${rsvpData.eventTitle}
Date: ${rsvpData.eventDate}
Total Attendee Count: ${rsvpData.guestCount}

Primary Attendee Details:
Name: ${rsvpData.name}
Email: ${rsvpData.email}
Dietary: ${rsvpData.primaryDietary ? rsvpData.primaryDietary : 'N/A'}

Guest Details:
${guestDetails}

Payment Details:
${isPayLater ? `Payment Method: Pay later / in person
Amount Due At Event: $${rsvpData.total.toFixed(2)}
Please bring payment to the event check-in.` : `Amount Paid: $${rsvpData.total.toFixed(2)}`}
Payment ID: ${rsvpData.paymentDetails.id}
Payment Time: ${paymentTime}

We look forward to seeing you at the event!
    `.trim();

    // Send to admin
    const adminTemplateParams = {
      to_email: EMAILJS_CONFIG.adminEmail,
      from_name: 'HCHS RSVP',
      from_email: rsvpData.email,
      subject: `NEW RSVP: ${rsvpData.name} for ${rsvpData.eventDate}`,
      message: emailContent
    };

    // Send to user
    const userTemplateParams = {
      to_email: rsvpData.email,
      from_name: 'HCHS RSVP',
      from_email: EMAILJS_CONFIG.adminEmail,
      subject: `RSVP Confirmation: ${rsvpData.eventTitle}`,
      message: confirmationContent
    };

    // Send both emails (do not throw; caller can decide what to show)
    const [adminResponse, userResponse] = await Promise.all([
      sendEmailSafe(adminTemplateParams),
      sendEmailSafe(userTemplateParams)
    ]);

    const adminOk = !(adminResponse && adminResponse.__error);
    const userOk = !(userResponse && userResponse.__error);

    if (!adminOk) console.error('Error sending RSVP admin email:', adminResponse.__error);
    if (!userOk) console.error('Error sending RSVP user email:', userResponse.__error);

    return adminOk && userOk;
  } catch (error) {
    console.error('Error sending RSVP email:', error);
    return false;
  }
};

export const sendMembershipEmail = async (membershipData) => {
  try {
    const secondPersonDetails = membershipData.secondPerson ? `
Second Person:
Name: ${membershipData.secondPerson.fullName}
Phone: ${membershipData.secondPerson.phone || 'N/A'}
Email: ${membershipData.secondPerson.email || 'N/A'}
    ` : '';

    const adminContent = `
New Membership Application

Application Type: ${membershipData.applicationType === 'new' ? 'New Member' : 'Renewal'}
Membership Type: ${membershipData.membershipType}
Amount: $${membershipData.amount.toFixed(2)}

First Person:
Name: ${membershipData.firstPerson.fullName}
Phone: ${membershipData.firstPerson.phone || 'N/A'}
Email: ${membershipData.firstPerson.email || 'N/A'}

${secondPersonDetails}

Address:
${membershipData.address.mailing || membershipData.address.streetAddress || 'N/A'}
${membershipData.address.city || 'N/A'}, ${membershipData.address.state || 'N/A'} 
${membershipData.address.zipCode || ''}${membershipData.address.zipExt ? `-${membershipData.address.zipExt}` : ''}

Payment Details:
Payment ID: ${membershipData.paymentDetails.id}
Payment Time: ${new Date(membershipData.paymentDetails.create_time).toLocaleString()}
Test Mode: ${membershipData.paymentDetails.isTestMode ? 'Yes' : 'No'}
    `.trim();

    const confirmationContent = `
Thank you for your membership application!

Your membership application has been received and processed:

Application Type: ${membershipData.applicationType === 'new' ? 'New Member' : 'Renewal'}
Membership Type: ${membershipData.membershipType}
Amount Paid: $${membershipData.amount.toFixed(2)}

Primary Member:
Name: ${membershipData.firstPerson.fullName}
Phone: ${membershipData.firstPerson.phone || 'N/A'}
Email: ${membershipData.firstPerson.email || 'N/A'}

${secondPersonDetails}

Address:
${membershipData.address.mailing || membershipData.address.streetAddress || 'N/A'}
${membershipData.address.city || 'N/A'}, ${membershipData.address.state || 'N/A'} 
${membershipData.address.zipCode || ''}${membershipData.address.zipExt ? `-${membershipData.address.zipExt}` : ''}

Payment Details:
Payment ID: ${membershipData.paymentDetails.id}
Payment Time: ${new Date(membershipData.paymentDetails.create_time).toLocaleString()}

We appreciate your support of the Harris County Historical Society!
    `.trim();

    // Send to admin
    const adminTemplateParams = {
      to_email: EMAILJS_CONFIG.adminEmail,
      from_name: 'HCHS Membership',
      from_email: membershipData.firstPerson.email,
      subject: `NEW MEMBERSHIP: ${membershipData.firstPerson.fullName}`,
      message: adminContent
    };

    // Send to user
    const userTemplateParams = {
      to_email: membershipData.firstPerson.email,
      from_name: 'HCHS Membership',
      from_email: EMAILJS_CONFIG.adminEmail,
      subject: `Membership Confirmation: ${membershipData.firstPerson.fullName}`,
      message: confirmationContent
    };

    // Send both emails (do not throw; caller can decide what to show)
    const [adminResponse, userResponse] = await Promise.all([
      sendEmailSafe(adminTemplateParams),
      sendEmailSafe(userTemplateParams)
    ]);

    const adminOk = !(adminResponse && adminResponse.__error);
    const userOk = !(userResponse && userResponse.__error);

    if (!adminOk) console.error('Error sending membership admin email:', adminResponse.__error);
    if (!userOk) console.error('Error sending membership user email:', userResponse.__error);

    return adminOk && userOk;
  } catch (error) {
    console.error('Error sending membership email:', error);
    return false;
  }
}; 